import type { FieldDefinition } from '../data/model';
import { isAttributeRuleGroupValid } from '../rules/validation';
import { isHexColour, normaliseHexColour } from '../styles/colour';
import { isLabelField } from './fields';
import { isLabelFontFamilyId, isLabelFontStyle, isLabelFontWeight } from './fonts';
import { supportsAuthoredItalic } from '../typography/fonts';
import type { LayerLabelConfiguration, PointLabelPlacement } from './model';

export const LABEL_LIMITS = {
  textSize: { minimum: 8, maximum: 72 },
  haloWidth: { minimum: 0, maximum: 8 },
  pointOffset: { minimum: 0, maximum: 48 },
  lineOffset: { minimum: -48, maximum: 48 },
  collisionSpacing: { minimum: 0, maximum: 32 },
} as const;

export const POINT_LABEL_PLACEMENTS: readonly PointLabelPlacement[] = [
  'auto',
  'above',
  'below',
  'left',
  'right',
  'centre',
];

export type LayerLabelPatch = Partial<Omit<LayerLabelConfiguration, 'enabled' | 'filter'>>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function isFiniteInRange(value: unknown, minimum: number, maximum: number): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum
  );
}

export function isLayerLabelConfigurationValid(
  value: unknown,
  fields: readonly FieldDefinition[],
): value is LayerLabelConfiguration {
  if (!isRecord(value)) return false;
  if (
    !hasOnlyKeys(value, [
      'enabled',
      'field',
      'filter',
      'fontFamily',
      'fontWeight',
      'fontStyle',
      'textSize',
      'textColour',
      'haloColour',
      'haloWidth',
      'pointPlacement',
      'pointOffset',
      'lineOffset',
      'collisionSpacing',
    ]) ||
    typeof value.enabled !== 'boolean' ||
    (value.field !== null && typeof value.field !== 'string') ||
    (typeof value.field === 'string' && !isLabelField(fields, value.field)) ||
    (value.enabled && value.field === null) ||
    (value.filter !== null && !isAttributeRuleGroupValid(value.filter, fields)) ||
    !isLabelFontFamilyId(value.fontFamily) ||
    !isLabelFontWeight(value.fontWeight) ||
    !isLabelFontStyle(value.fontStyle) ||
    (value.fontStyle === 'italic' && !supportsAuthoredItalic(value.fontFamily)) ||
    !isFiniteInRange(
      value.textSize,
      LABEL_LIMITS.textSize.minimum,
      LABEL_LIMITS.textSize.maximum,
    ) ||
    !isHexColour(value.textColour) ||
    !isHexColour(value.haloColour) ||
    !isFiniteInRange(
      value.haloWidth,
      LABEL_LIMITS.haloWidth.minimum,
      LABEL_LIMITS.haloWidth.maximum,
    ) ||
    typeof value.pointPlacement !== 'string' ||
    !POINT_LABEL_PLACEMENTS.includes(value.pointPlacement as PointLabelPlacement) ||
    !isFiniteInRange(
      value.pointOffset,
      LABEL_LIMITS.pointOffset.minimum,
      LABEL_LIMITS.pointOffset.maximum,
    ) ||
    !isFiniteInRange(
      value.lineOffset,
      LABEL_LIMITS.lineOffset.minimum,
      LABEL_LIMITS.lineOffset.maximum,
    ) ||
    !isFiniteInRange(
      value.collisionSpacing,
      LABEL_LIMITS.collisionSpacing.minimum,
      LABEL_LIMITS.collisionSpacing.maximum,
    )
  ) {
    return false;
  }
  return true;
}

export function updateLayerLabelConfiguration(
  current: LayerLabelConfiguration,
  patch: LayerLabelPatch,
  fields: readonly FieldDefinition[],
): LayerLabelConfiguration | null {
  if (
    !hasOnlyKeys(patch as Record<string, unknown>, [
      'field',
      'fontFamily',
      'fontWeight',
      'fontStyle',
      'textSize',
      'textColour',
      'haloColour',
      'haloWidth',
      'pointPlacement',
      'pointOffset',
      'lineOffset',
      'collisionSpacing',
    ])
  ) {
    return null;
  }
  const textColour =
    patch.textColour === undefined ? undefined : normaliseHexColour(patch.textColour);
  const haloColour =
    patch.haloColour === undefined ? undefined : normaliseHexColour(patch.haloColour);
  if (textColour === null || haloColour === null) return null;
  const next: LayerLabelConfiguration = {
    ...current,
    ...patch,
    ...(textColour === undefined ? {} : { textColour }),
    ...(haloColour === undefined ? {} : { haloColour }),
  };
  return isLayerLabelConfigurationValid(next, fields) ? next : null;
}
